/**
 * AmourLinen sitewide tier offer (v3.6 — localized + auto tote, loop-safe)
 * - Counts every cart item except gift cards and excluded handles (tote)
 * - Progress fill measured from real label positions: ends at the right
 *   edge of the highest unlocked tier label
 * - Auto-adds the complimentary Linen Tote at TOTE_THRESHOLD pieces,
 *   auto-removes it below; respects manual removal for the session
 * - v3.5: race-condition fix — lock acquired before any await, fresh cart
 *   re-fetched inside the lock, self-healing removes surplus auto-totes.
 * - v3.6: LOOP FIX — the status line innerHTML was written unconditionally on
 *   every sync. Because the status block lives inside <cart-drawer>, each write
 *   re-triggered this file's MutationObserver AND cart-drawer.js's observer,
 *   which fed each other into a perpetual ~1/sec /cart.js loop for any visitor
 *   with items in cart (the crash / heap-growth cause). The write is now
 *   guarded by a per-element content comparison, so it only mutates the DOM
 *   when the text actually changes.
 */

(function () {
  'use strict';

  if (window.__alTierOfferInit) return;
  window.__alTierOfferInit = true;

  var TOTE_HANDLE = 'the-linen-tote';
  var TOTE_THRESHOLD = 3;
  var TOTE_PROP = '_complimentary_tote';
  var TOTE_DISMISS_KEY = 'al_tote_dismissed';

  var toteVariantId = null;
  var toteBusy = false;

  function esc(s) {
    const d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }

  function readConfig(root) {
    const fallback = {
      excludeHandles: [],
      tiers: [],
      maxThreshold: 5,
      redPhrase: '+ tote bag',
      emptyPre: 'Add any',
      emptyStrong: '2 pieces',
      emptyPost: 'to unlock your first discount.',
      progressLead: '[count] added.',
      progressRest: 'Add [needed] more summer [essword] to unlock [next].',
      wordItemOne: 'item',
      wordItemMany: 'items',
      wordEssOne: 'essential',
      wordEssMany: 'essentials'
    };
    try {
      const el = root.querySelector('script[data-tier-offer-config]');
      if (!el) return fallback;
      return Object.assign(fallback, JSON.parse(el.textContent));
    } catch (e) {
      return fallback;
    }
  }

  function wrapRed(escapedText, phrase) {
    if (!phrase) return escapedText;
    const p = esc(phrase);
    return escapedText.split(p).join('<span class="to-red">' + p + '</span>');
  }

  function eligibleCount(cart, exclude) {
    let count = 0;
    if (cart && Array.isArray(cart.items)) {
      cart.items.forEach(item => {
        if (item.gift_card) return;
        if (exclude.has(item.handle)) return;
        count += item.quantity;
      });
    }
    return count;
  }

  /**
   * Fill width in %, measured from rendered label positions: the fill
   * ends at the right edge of the highest unlocked tier's label, and
   * interpolates between label edges partway to the next tier.
   * Falls back to even spacing if the bar isn't measurable.
   */
  function setFill(root, count, thresholds) {
    const fill = root.querySelector('[data-tier-offer-fill]');
    if (!fill) return;
    if (!thresholds.length) { fill.style.width = '0%'; return; }

    const track = root.querySelector('.tier-offer__mini-track');
    const labels = Array.from(root.querySelectorAll('.tier-offer__mini-label'));
    let anchors = null;

    if (track && labels.length === thresholds.length) {
      const tr = track.getBoundingClientRect();
      if (tr.width > 0) {
        anchors = labels.map(function (l) {
          const lr = l.getBoundingClientRect();
          return Math.min(((lr.right - tr.left) / tr.width) * 100, 100);
        });
      }
    }
    if (!anchors) {
      anchors = thresholds.map(function (t, i) {
        return ((i + 1) / thresholds.length) * 100;
      });
    }

    const n = thresholds.length;
    let pct;

    if (count <= 0) {
      pct = 0;
    } else if (count >= thresholds[n - 1]) {
      pct = 100;
    } else if (count < thresholds[0]) {
      pct = (count / thresholds[0]) * anchors[0] * 0.6;
    } else {
      let lower = 0;
      for (let i = 0; i < n; i++) {
        if (count >= thresholds[i]) lower = i;
      }
      if (lower >= n - 1) {
        pct = 100;
      } else {
        const span = thresholds[lower + 1] - thresholds[lower];
        const into = count - thresholds[lower];
        pct = anchors[lower] + (into / span) * (anchors[lower + 1] - anchors[lower]);
      }
    }

    // Guard the style write too — avoids redundant layout writes on every sync.
    const nextWidth = Math.max(0, Math.min(pct, 100)) + '%';
    if (fill.style.width !== nextWidth) {
      fill.style.width = nextWidth;
    }
  }

  // ============ COMPLIMENTARY TOTE ============

  async function getToteVariantId() {
    if (toteVariantId) return toteVariantId;
    try {
      const r = await fetch('/products/' + TOTE_HANDLE + '.js', { headers: { 'Accept': 'application/json' } });
      if (!r.ok) return null;
      const p = await r.json();
      if (!p || !Array.isArray(p.variants)) return null;
      const v = p.variants.find(function (x) { return x.available; }) || p.variants[0];
      toteVariantId = v ? v.id : null;
      return toteVariantId;
    } catch (e) { return null; }
  }

  function autoToteLines(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    return cart.items.filter(function (item) {
      return item.handle === TOTE_HANDLE && item.properties && item.properties[TOTE_PROP];
    });
  }

  function findAnyTote(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    return cart.items.find(function (item) { return item.handle === TOTE_HANDLE; }) || null;
  }

  function isDismissed() {
    try { return sessionStorage.getItem(TOTE_DISMISS_KEY) === '1'; } catch (e) { return false; }
  }
  function setDismissed(v) {
    try {
      if (v) sessionStorage.setItem(TOTE_DISMISS_KEY, '1');
      else sessionStorage.removeItem(TOTE_DISMISS_KEY);
    } catch (e) {}
  }
  function wasAutoAdded() {
    try { return sessionStorage.getItem('al_tote_added') === '1'; } catch (e) { return false; }
  }
  function setAutoAdded(v) {
    try {
      if (v) sessionStorage.setItem('al_tote_added', '1');
      else sessionStorage.removeItem('al_tote_added');
    } catch (e) {}
  }

  async function fetchCartFresh() {
    try {
      const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!r.ok) return null;
      return await r.json();
    } catch (e) { return null; }
  }

  async function changeLine(key, quantity) {
    try {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      return r.ok;
    } catch (e) { return false; }
  }

  async function manageTote(cart, count) {
    if (toteBusy) return;
    toteBusy = true; // lock BEFORE any await — closes the race window

    try {
      // Decisions are made on a FRESH cart, not the possibly stale snapshot
      const fresh = await fetchCartFresh();
      if (!fresh) return;

      const exclude = new Set([TOTE_HANDLE]);
      const freshCount = eligibleCount(fresh, exclude);
      const autoLines = autoToteLines(fresh);
      const autoQty = autoLines.reduce(function (s, l) { return s + l.quantity; }, 0);
      const anyTote = findAnyTote(fresh);
      let mutated = false;

      if (freshCount >= TOTE_THRESHOLD) {
        // Self-heal: exactly ONE auto-added tote allowed. Surplus (race leftovers)
        // is trimmed. Lines without TOTE_PROP are manual purchases — untouched.
        if (autoQty > 1) {
          if (await changeLine(autoLines[0].key, 1)) mutated = true;
          for (let i = 1; i < autoLines.length; i++) {
            if (await changeLine(autoLines[i].key, 0)) mutated = true;
          }
        } else if (autoQty === 0) {
          if (!anyTote && wasAutoAdded() && !isDismissed()) {
            setDismissed(true);
            return;
          }
          if (anyTote || isDismissed()) return;

          const variantId = await getToteVariantId();
          if (!variantId) return;

          const props = {};
          props[TOTE_PROP] = '1';
          try {
            const r = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: props }] })
            });
            if (r.ok) {
              setAutoAdded(true);
              mutated = true;
            }
          } catch (e) {}
        }
        // autoQty === 1 → correct state, nothing to do
      } else {
        // Below threshold: remove ALL auto-added tote lines (manual ones stay)
        if (autoQty > 0) {
          for (let i = 0; i < autoLines.length; i++) {
            if (await changeLine(autoLines[i].key, 0)) mutated = true;
          }
          if (mutated) {
            setAutoAdded(false);
            setDismissed(false);
          }
        }
      }

      if (mutated) {
        await refreshDrawerSections();
      }
    } finally {
      toteBusy = false;
    }
  }

  async function refreshDrawerSections() {
    try {
      const r = await fetch(window.location.pathname + '?sections=cart-drawer,cart-icon-bubble');
      if (!r.ok) return;
      const sections = await r.json();
      if (sections['cart-drawer']) {
        const fresh = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html').querySelector('#CartDrawer');
        const current = document.querySelector('#CartDrawer');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      }
      if (sections['cart-icon-bubble']) {
        const freshB = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('#cart-icon-bubble');
        const currentB = document.querySelector('#cart-icon-bubble');
        if (freshB && currentB) currentB.innerHTML = freshB.innerHTML;
      }
    } catch (e) {}
  }

  // ============ SYNC ============

  let syncTimers = [];

  async function syncAll() {
    let cart;
    try {
      const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!r.ok) return;
      cart = await r.json();
    } catch (e) { return; }

    const roots = document.querySelectorAll('[data-tier-offer]');
    let globalCount = null;

    roots.forEach(function (root) {
      const c = readConfig(root);
      const exclude = new Set(c.excludeHandles || []);
      const count = eligibleCount(cart, exclude);
      if (globalCount === null) globalCount = count;

      const tiers = (c.tiers || []).slice().sort(function (a, b) { return a.threshold - b.threshold; });
      let best = null;
      tiers.forEach(function (t) { if (count >= t.threshold) best = t; });

      root.querySelectorAll('[data-to-threshold]').forEach(function (el) {
        const th = parseInt(el.dataset.toThreshold, 10);
        const nextState = (!isNaN(th) && count >= th) ? 'unlocked' : 'locked';
        // Guard: dataset writes are attribute mutations; only write on change.
        if (el.dataset.toState !== nextState) el.dataset.toState = nextState;
      });

      setFill(root, count, tiers.map(function (t) { return t.threshold; }));

      const statusEl = root.querySelector('[data-tier-offer-status]');
      if (!statusEl) return;
      let html = '';

      if (count === 0) {
        html = esc(c.emptyPre) + ' <strong>' + esc(c.emptyStrong) + '</strong> ' + esc(c.emptyPost);
      } else if (!best) {
        const first = tiers[0];
        const needed = first ? Math.max(first.threshold - count, 0) : 0;
        const countTxt = count + ' ' + (count === 1 ? c.wordItemOne : c.wordItemMany);
        const essword = needed === 1 ? c.wordEssOne : c.wordEssMany;
        const lead = String(c.progressLead || '').replace('[count]', countTxt);
        const rest = String(c.progressRest || '')
          .replace('[needed]', String(needed))
          .replace('[essword]', essword)
          .replace('[next]', first ? first.amount : '');
        html = '<strong>' + esc(lead) + '</strong> ' + esc(rest);
      } else {
        html = '<strong>' + wrapRed(esc(best.lead), c.redPhrase) + '</strong> ' + esc(best.rest);
      }

      // ---- LOOP FIX ----
      // Only touch the DOM when the rendered text actually changes. Writing
      // innerHTML unconditionally re-triggered this file's observer and
      // cart-drawer.js's observer, which re-scheduled syncAll, forever.
      const nextHtml = html.replace(/\s+/g, ' ').trim();
      if (statusEl.__alLastHtml !== nextHtml) {
        statusEl.__alLastHtml = nextHtml;
        statusEl.innerHTML = nextHtml;
      }
    });

    if (globalCount === null) {
      const exclude = new Set([TOTE_HANDLE]);
      globalCount = eligibleCount(cart, exclude);
    }
    manageTote(cart, globalCount);
  }

  function scheduleSync() {
    syncTimers.forEach(clearTimeout);
    syncTimers = [
      setTimeout(syncAll, 300),
      setTimeout(syncAll, 1000)
    ];
  }

  const origFetch = window.fetch;
  window.fetch = function () {
    const url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
    const result = origFetch.apply(this, arguments);
    if (/\/cart\/(add|change|update|clear)/.test(url)) {
      result.then(scheduleSync).catch(function () {});
    }
    return result;
  };

  let drawerObserved = null;
  function bindDrawerObserver() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer === drawerObserved) return;
    drawerObserved = drawer;
    new MutationObserver(scheduleSync).observe(drawer, { childList: true, subtree: true });
  }

  ['cart:refresh', 'cart:updated', 'cart:item-added', 'on:cart:add', 'cart:change', 'line-item:change'].forEach(function (evt) {
    document.addEventListener(evt, function () {
      bindDrawerObserver();
      scheduleSync();
    });
  });

  function boot() {
    bindDrawerObserver();
    syncAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();